/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, ArrowLeftRight, AlertTriangle } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STAGES } from "./WorkQueue";

const PRIO_TONE = {
    urgent: "bg-rose-100 text-rose-800 border-rose-200",
    high: "bg-amber-100 text-amber-800 border-amber-200",
    normal: "bg-zinc-100 text-zinc-700 border-zinc-200",
    low: "bg-zinc-50 text-zinc-500 border-zinc-200",
};

function slaState(item) {
    if (!item.sla_due_at) return null;
    const ms = new Date(item.sla_due_at).getTime() - Date.now();
    const days = Math.round(ms / 86400000);
    if (ms < 0) return { text: `${Math.abs(days)}d overdue`, breached: true };
    if (days <= 1) return { text: days <= 0 ? "due today" : "1d left", breached: false };
    return { text: `${days}d left`, breached: false };
}

export default function ProductionLine() {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [w, c] = await Promise.all([
                api.get("/work-queue/", { params: { open_only: false } }),
                api.get("/clients/"),
            ]);
            // Show all open + recent filed (last 7 days)
            const cutoff = Date.now() - 7 * 86400000;
            setItems(w.data.filter((i) => i.stage !== "filed" || (i.closed_at && new Date(i.closed_at).getTime() > cutoff)));
            setClients(c.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const clientName = useCallback((id) => clients.find((c) => c.id === id)?.name || "—", [clients]);

    async function moveTo(item, stage) {
        if (stage === item.stage) return;
        try {
            await api.patch(`/work-queue/${item.id}`, { stage });
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div data-testid="production-line-page">
            <PageHeader
                eyebrow="Production line"
                title="Kanban board"
                description="One card per piece of work, flowing left to right. Move a card by selecting a stage on its dropdown — same as a McDonald's KDS or Amazon FC pick wall."
                testId="production-line-header"
            />
            {loading ? (
                <div className="flex items-center justify-center py-16 text-[#5E636E]"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 overflow-x-auto" data-testid="production-board">
                    {STAGES.map((s) => {
                        const col = items.filter((i) => i.stage === s.value);
                        return (
                            <div key={s.value} className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] flex flex-col min-w-[260px]" data-testid={`prod-col-${s.value}`}>
                                <div className="flex items-center justify-between px-3 py-2 border-b border-[#E4E4E7] sticky top-0 bg-[#FAFAFA]">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">{s.label}</div>
                                        <div className="font-display text-lg tabular-nums">{col.length}</div>
                                    </div>
                                    <span className={`inline-flex h-6 w-6 rounded-full border ${s.tone}`}></span>
                                </div>
                                <div className="flex-1 overflow-y-auto p-2 space-y-2 max-h-[70vh]">
                                    {col.length === 0 && <div className="text-center text-[11px] text-[#A4A6AB] py-6">— empty —</div>}
                                    {col.map((i) => {
                                        const sla = slaState(i);
                                        return (
                                            <div key={i.id} className={`rounded-md border bg-white p-2.5 shadow-sm ${sla?.breached ? "border-rose-300" : "border-[#E4E4E7]"}`} data-testid={`prod-card-${i.id}`}>
                                                <div className="flex items-start justify-between gap-1.5">
                                                    <div className="text-[12px] font-medium truncate leading-tight flex-1">{i.title}</div>
                                                    <span className={`shrink-0 inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] ${PRIO_TONE[i.priority] || PRIO_TONE.normal}`}>{i.priority}</span>
                                                </div>
                                                <div className="mt-1 text-[11px] text-[#5E636E] truncate">{clientName(i.client_id)}</div>
                                                {i.blocking_reason && (
                                                    <div className="mt-1 text-[10px] text-amber-700 line-clamp-2 leading-tight">⏸ {i.blocking_reason}</div>
                                                )}
                                                <div className="mt-1.5 flex items-center justify-between gap-1">
                                                    {sla && (
                                                        <span className={`text-[10px] inline-flex items-center gap-0.5 ${sla.breached ? "text-rose-700 font-medium" : "text-[#5E636E]"}`}>
                                                            {sla.breached && <AlertTriangle className="h-3 w-3" />}
                                                            {sla.text}
                                                        </span>
                                                    )}
                                                    <Select value={i.stage} onValueChange={(v) => moveTo(i, v)}>
                                                        <SelectTrigger className="h-6 w-[110px] text-[10px] ml-auto" data-testid={`prod-stage-${i.id}`}>
                                                            <ArrowLeftRight className="h-2.5 w-2.5" />
                                                            Move
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {STAGES.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <p className="mt-4 text-xs text-[#5E636E]">Cards auto-flow as the system records events: PM job status change → moves card · NPM filed → closes card · new request → drops new card in <em>Waiting on client</em>. Staff only touch what needs touching.</p>
        </div>
    );
}
